"use client";

import * as Headless from "@headlessui/react";
import React, { useEffect, useState } from "react";
import { NavbarItem } from "./navbar";
import { cn } from "@/lib/utils";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useLocation } from "@tanstack/react-router";

function OpenMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
    </svg>
  );
}

function CloseMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  );
}

function MobileSidebar({
  open,
  close,
  children,
}: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
  const { hasSupplier } = useGlobalSupplier();

  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden">
      <Headless.DialogBackdrop
        transition
        className="data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in fixed inset-0 bg-black/30 transition"
      />
      <Headless.DialogPanel
        transition
        className="data-closed:-translate-x-full fixed inset-y-0 w-full max-w-80 p-2 transition duration-300 ease-in-out"
      >
        <div
          className={cn(
            "shadow-xs flex h-full flex-col rounded-lg ring-1 ring-zinc-950/5 dark:ring-white/10",
            {
              "bg-brand": hasSupplier,
              "bg-fuchsia-950": !hasSupplier,
            },
          )}
        >
          <div className="-mb-3 px-4 pt-3 [&_[data-slot=icon]]:fill-white">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <CloseMenuIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  );
}

export function SidebarLayout({
  navbar,
  sidebar,
  children,
  className,
}: React.PropsWithChildren<{
  navbar: React.ReactNode;
  sidebar: React.ReactNode;
  className?: string;
}>) {
  const { hasSupplier } = useGlobalSupplier();
  const { isAdmin } = useTenant();

  const [showSidebar, setShowSidebar] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setShowSidebar(false);
  }, [pathname]);

  return (
    <div
      className={cn(
        "relative isolate flex min-h-svh w-full bg-white max-lg:flex-col lg:h-full",
        className,
        {
          "lg:bg-brand": hasSupplier,
          "lg:bg-fuchsia-950": !hasSupplier,
        },
      )}
    >
      {/* Sidebar on desktop */}
      <div
        className={cn("fixed inset-y-0 left-0 w-64 max-lg:hidden", {
          "lg:bg-brand": hasSupplier,
          "lg:bg-fuchsia-950": !hasSupplier,
        })}
      >
        {sidebar}
      </div>

      {/* Sidebar on mobile */}
      <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
        {sidebar}
      </MobileSidebar>

      {/* Navbar on mobile */}
      <header className="flex items-center px-4 lg:hidden">
        <div className="py-2.5">
          <NavbarItem
            onClick={() => setShowSidebar(true)}
            aria-label="Open navigation"
          >
            <OpenMenuIcon />
          </NavbarItem>
        </div>
        <div className="min-w-0 flex-1">{navbar}</div>
      </header>

      {/* Content */}
      <main className="flex min-w-0 flex-1 flex-col bg-white lg:min-h-0 lg:pl-64">
        {hasSupplier && isAdmin ? <AdminAsSupplierBanner /> : null}

        {children}
      </main>
    </div>
  );
}

function AdminAsSupplierBanner() {
  const { actions } = useGlobalSupplier();

  return (
    <div className="flex w-full justify-center bg-sky-400 p-1 text-center text-sky-950">
      ⚠️ You are an admin viewing as supplier ⚠️
      <button
        onClick={() => {
          actions.setSupplier(null);
        }}
        className="ml-2 underline"
      >
        back to admin &rarr;
      </button>
    </div>
  );
}
